import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { ManualLedger } from '../types';
import { registerCalibriFonts, setCalibriFont } from '../utils/fontLoader';

export const formatDate = (date: any): string => {
  if (!date) return 'N/A';
  try {
    // Handle Firestore Timestamp
    if (date.toDate && typeof date.toDate === 'function') {
      const convertedDate = date.toDate();
      if (convertedDate instanceof Date && !isNaN(convertedDate.getTime())) {
        return convertedDate.toLocaleDateString();
      }
    }
    // Handle Date objects or strings
    const parsedDate = new Date(date);
    if (parsedDate instanceof Date && !isNaN(parsedDate.getTime())) {
      return parsedDate.toLocaleDateString();
    }
    return 'N/A';
  } catch (error) {
    console.warn('Invalid date format:', date, error);
    return 'N/A';
  }
};

// Helper: fetch an image URL and convert to a data URL (handles CORS)
const fetchImageAsDataUrl = async (url: string): Promise<{ dataUrl: string; format: 'PNG' | 'JPEG' }> => {
  const response = await fetch(url, { mode: 'cors' });
  if (!response.ok) throw new Error(`Failed to fetch image: ${response.status}`);
  const contentType = response.headers.get('content-type') || '';
  const blob = await response.blob();
  const dataUrl: string = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
  const isPng = contentType.includes('png') || url.toLowerCase().endsWith('.png');
  return { dataUrl, format: isPng ? 'PNG' : 'JPEG' };
};

export const generateManualLedgerPDF = async (ledger: ManualLedger): Promise<jsPDF> => {
    const doc = new jsPDF();

    // Register Calibri fonts
    await registerCalibriFonts(doc);
  
    // Set up the page
    const pageWidth = (doc.internal.pageSize as any).getWidth
      ? (doc.internal.pageSize as any).getWidth()
      : (doc.internal.pageSize as any).width;
    const margin = 5;
    let yPosition = margin;
  
    // Add logo if available using data URL (more robust for remote images)
    if (ledger.logoUrl) {
      try {
        const { dataUrl, format } = await fetchImageAsDataUrl(ledger.logoUrl);
        // Default width ~53 to match previous layout
        const logoWidth = 65;
        // Estimate height using an offscreen image to preserve aspect ratio
        const tempImg = new Image();
        tempImg.src = dataUrl;
        await new Promise((resolve) => {
          tempImg.onload = () => resolve(true);
          tempImg.onerror = () => resolve(true);
        });
        const logoHeight = tempImg.width && tempImg.height ? (tempImg.height * logoWidth) / tempImg.width : 16;
        doc.addImage(dataUrl, format, margin, yPosition, logoWidth, logoHeight);
      } catch (error) {
        console.error('Error loading logo:', error);
      }
    }
  
    // Add company name on the right side with underline
    doc.setFontSize(13);
    setCalibriFont(doc, 'bold');
    doc.setTextColor(2, 18, 61);
    const companyName = (ledger as any).logisticsCompany || ledger.companyName || 'Unknown';
    const companyNameLeftX = pageWidth - margin - doc.getTextWidth(companyName);
    const companyNameOffsetY = 6;
    doc.text(companyName, companyNameLeftX, yPosition + companyNameOffsetY);
    
    // Add underline under company name
    const companyNameWidth = doc.getTextWidth(companyName);
    (doc as any).setDrawColor(2, 18, 61);
    doc.setLineWidth(0.75);
    doc.line(companyNameLeftX, yPosition + companyNameOffsetY + 1, companyNameLeftX + companyNameWidth, yPosition + companyNameOffsetY + 1);

    // Add company address and contact details below the company name
    doc.setTextColor(2, 18, 61);
    doc.setFontSize(9);
    setCalibriFont(doc, 'bold');
    
    // Determine if this is Vector Logistics to use appropriate address format
    const isVector = companyName === 'VECTOR LOGISTICS';
    
    const addressLines = isVector ? [
      'Office M-7, M Floor, Khan Arcade,',
      'Firdous Market, Gulberg III,',
      'Lahore, Punjab, Pakistan',
      'Tel 042-35644728, 35881129', 
    ] : [
      'Office M-7, M Floor, Khan Arcade, Firdous Market,',
      'Gulberg III, Lahore, Punjab, Pakistan',
      'Tel 042-35644728, 35881129', 
    ];
    
    // Position address text to start directly under the company name
    let addressY = yPosition + companyNameOffsetY + 6;
    addressLines.forEach(line => {
      doc.text(line, companyNameLeftX, addressY);
      addressY += 3.5;
    });

    yPosition = addressY + 8; // Add more gap after address details

    // Add party name as title
    doc.setFontSize(12);
    setCalibriFont(doc, 'bold');
    doc.setTextColor(0, 0, 0); // Reset to black
    doc.text(ledger.partyName || 'Unknown', margin, yPosition);

    // Add Statement Of Account and As Of date on the same line
    yPosition += 5;
    doc.setFontSize(10);
    setCalibriFont(doc, 'bold');
    doc.text('STATEMENT OF ACCOUNT', margin, yPosition);

    // Add "As Of:" date on the right side of the same line
    doc.setFontSize(9);
    setCalibriFont(doc, 'bold');
    const asOfText = `AS OF:  ${new Date().toLocaleDateString()}`;
    const asOfTextWidth = doc.getTextWidth(asOfText);
    doc.text(asOfText, pageWidth - margin - asOfTextWidth, yPosition);
  
    // Prepare ledger entries data for the table
    let runningBalance = ledger.openingBalance || 0;
    const ledgerData = ledger.entries.map(entry => {
      // Apply the correct formula: credit adds, debit subtracts
      runningBalance += entry.credit - entry.debit;
      return [
        formatDate(entry.date),
        entry.particulars || '',
        entry.debit > 0 ? Math.round(entry.debit).toLocaleString() : '',
        entry.credit > 0 ? Math.round(entry.credit).toLocaleString() : '',
        Math.round(runningBalance).toLocaleString()
      ];
    });
  
    // Calculate totals
    // Add opening balance row if it exists and is not zero
    if (ledger.openingBalance && ledger.openingBalance !== 0) {
      // Place label under Particulars column, leave Date empty
      ledgerData.unshift(['', 'OPENING BALANCE', '', '', Math.round(ledger.openingBalance).toLocaleString()]);
    }
  
    // Remove subtotal row entirely as per requirements
  
    // Add ledger table
    yPosition += 5;
    const availableWidth = pageWidth - margin * 2;

    // Column proportions: 20, 50, 15, 15, 20 out of 120 total
    const totalUnits = 20 + 50 + 15 + 15 + 20; // 120
    const columnStyles = {
      0: { cellWidth: (20 / totalUnits) * availableWidth }, // Date
      1: { cellWidth: (50 / totalUnits) * availableWidth }, // Particulars
      2: { cellWidth: (15 / totalUnits) * availableWidth }, // Debit
      3: { cellWidth: (15 / totalUnits) * availableWidth }, // Credit
      4: { cellWidth: (20 / totalUnits) * availableWidth }  // Balance
    };

    autoTable(doc, {
      startY: yPosition,
      head: [['DATE', 'PARTICULARS', 'DEBIT', 'CREDIT', 'BALANCE']],
      body: ledgerData,
      theme: 'grid',
      headStyles: {
        fillColor: [252, 180, 116],
        textColor: [0, 0, 0],
        fontSize: 9,
        font: 'calibri'
      },
      styles: {
        fontSize: 8,
        cellPadding: 2,
        halign: 'center',
        font: 'calibri'
      },
      margin: { left: margin, right: margin },
      tableWidth: availableWidth,
      columnStyles,
      didParseCell: function (data) {
        // Bold Balance column cells with larger font
        if (data.section === 'body' && data.column.index === 4) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fontSize = 9; // Slightly larger font
          data.cell.styles.textColor = [0, 0, 0]; // Black color (not red)
        }
        // Bold Balance header with larger font
        if (data.section === 'head' && data.column.index === 4) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fontSize = 10; // Slightly larger font for header
          data.cell.styles.textColor = [0, 0, 0]; // Black color for header
        }
      }
    });
  
    // Add notes to the last page after table generation
    // Set font for notes
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    
    // // Add system note
    // const systemNote = 'This is system generated ledger and does not require any signature.';
    // doc.text(systemNote, margin + 2, notesY + 6);
    
    // // Add content note
    // const contentNote = 'Content of the ledger will be considered correct if no errors are reported within 2 working days from ledger date.';
    // doc.text(contentNote, margin + 2, notesY + 11);

    return doc;
  };

// Excel export function for manual ledger
export const generateManualLedgerExcel = (ledger: ManualLedger): void => {
  // Create a new workbook
  const workbook = XLSX.utils.book_new();
  
  // Prepare ledger entries data
  let runningBalance = ledger.openingBalance || 0;
  const ledgerData = ledger.entries.map(entry => {
    // Apply the correct formula: credit adds, debit subtracts
    runningBalance += entry.credit - entry.debit;
    return [
      formatDate(entry.date),
      entry.particulars || '',
      entry.debit > 0 ? entry.debit : '',
      entry.credit > 0 ? entry.credit : '',
      runningBalance
    ];
  });

  // Add opening balance row if it exists and is not zero
  if (ledger.openingBalance && ledger.openingBalance !== 0) {
    ledgerData.unshift(['', 'OPENING BALANCE', '', '', ledger.openingBalance]);
  }

  // Define headers
  const headers = ['DATE', 'PARTICULARS', 'DEBIT', 'CREDIT', 'BALANCE'];
  
  // Create worksheet data
  const worksheetData = [headers, ...ledgerData];
  
  // Create worksheet
  const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
  
  // Set column widths
  const columnWidths = [
    { wch: 12 }, // DATE
    { wch: 40 }, // PARTICULARS
    { wch: 12 }, // DEBIT
    { wch: 12 }, // CREDIT
    { wch: 15 }  // BALANCE
  ];
  
  worksheet['!cols'] = columnWidths;
  
  // Add worksheet to workbook
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Manual Ledger');
  
  // Generate filename
  const sanitizedPartyName = (ledger.partyName || 'Unknown').replace(/[^a-zA-Z0-9]/g, '');
  const date = new Date().toISOString().split('T')[0];
  const filename = `Manual_Ledger_${sanitizedPartyName}_${date}.xlsx`;
  
  // Save the file
  XLSX.writeFile(workbook, filename);
};